/**
 * Translation function type from react-i18next
 * Takes a translation key and returns translated string
 */
type TranslationFunction = (key: string) => string

/**
 * Validation error type - empty string means no error
 */
type ValidationError = string

/**
 * Validates email format and presence
 * @param value - Email to validate (coerced to string)
 * @param t - i18n translation function
 * @returns Error message or empty string if valid
 */
export const validateEmail = (value: unknown, t: TranslationFunction): ValidationError => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!value) {
    return t('validation.email_required')
  }
  if (!emailRegex.test(String(value))) {
    return t('validation.email_invalid')
  }
  return ''
}

/**
 * Validates password strength during registration
 * Requires: min 8 chars, uppercase, lowercase, and number
 * @param value - Password to validate (coerced to string)
 * @param t - i18n translation function
 * @param isRegistering - Only validate password strength if registering
 * @returns Error message or empty string if valid
 */
export const validatePassword = (
  value: unknown,
  t: TranslationFunction,
  isRegistering: boolean = false
): ValidationError => {
  if (!isRegistering) return ''

  if (!value) {
    return t('validation.password_required')
  }
  const passwordStr = String(value)
  if (passwordStr.length < 8) {
    return t('validation.password_min_length')
  }
  if (!/[A-ZÄÖÅ]/.test(passwordStr)) {
    return t('validation.password_needs_uppercase')
  }
  if (!/[a-zäöå]/.test(passwordStr)) {
    return t('validation.password_needs_lowercase')
  }
  if (!/[0-9]/.test(passwordStr)) {
    return t('validation.password_needs_number')
  }
  if (!/[^A-Za-zäöåÄÖÅ0-9\s]/.test(passwordStr)) {
    return t('validation.password_needs_special')
  }
  return ''
}

/**
 * Validates password confirmation matches original password
 * @param value - Confirm password field value (coerced to string)
 * @param passwordValue - Original password value to match against (coerced to string)
 * @param t - i18n translation function
 * @returns Error message or empty string if valid
 */
export const validateConfirmPassword = (
  value: unknown,
  passwordValue: unknown,
  t: TranslationFunction
): ValidationError => {
  if (!value) {
    return t('validation.confirm_password_required')
  }
  if (String(value) !== String(passwordValue)) {
    return t('validation.passwords_dont_match')
  }
  return ''
}
